import { useCallback, useRef, useState } from "react"
import { format, isValid, parseISO } from "date-fns"
import {
	IconTrash2Outline18 as Trash2Icon,
	IconArrowTurnLeftOutline18 as UndoIcon,
} from "nucleo-ui-outline-18"

import { AlertDialog, Button, Dialog, IconButton } from "@productlane/ui"

import { toast } from "@/components/toast"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useImportPortalId } from "../../import-portal"
import { batchChangelogImports } from "./changelog-import-batches"

interface ChangelogPreviewDialogProps {
	isOpen: boolean
	onOpenChange: () => void
	changelogs: ChangelogImport[]
	onSuccess: () => void
}

export interface ChangelogImport {
	title: string
	description?: string
	imageUrl?: string
	dateIso?: string
	published?: boolean
}

function parseDate(dateString: string): Date | null {
	// Try parsing as ISO format first
	const isoDate = parseISO(dateString)
	if (isValid(isoDate)) {
		return isoDate
	}

	// Try parsing with native Date constructor (handles various formats)
	const nativeDate = new Date(dateString)
	if (isValid(nativeDate)) {
		return nativeDate
	}

	return null
}

function stripHtml(html: string): string {
	// Remove HTML tags
	const stripped = html.replace(/<[^>]*>/g, "")
	// Decode common HTML entities
	const decoded = stripped
		.replace(/&nbsp;/g, " ")
		.replace(/&amp;/g, "&")
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.replace(/&quot;/g, '"')
		.replace(/&#39;/g, "'")
	return decoded.trim()
}

export function ChangelogPreviewDialog({
	isOpen,
	onOpenChange,
	changelogs,
	onSuccess,
}: ChangelogPreviewDialogProps) {
	const [deleted, setDeleted] = useState<number[]>([])
	const [isConfirmCloseDialogOpen, setIsConfirmCloseDialogOpen] =
		useState(false)

	const portalInstanceId = useImportPortalId()

	// Rows already saved, so a retry after a failed batch never creates them twice.
	const [uploaded, setUploaded] = useState<number[]>([])
	const [isUploading, setIsUploading] = useState(false)
	const cancelledRef = useRef(false)

	const bulkCreateChangelogsMutation =
		api.workspace.bulkCreateChangelogs.useMutation()

	const handleDelete = useCallback(
		(i: number) => {
			if (deleted.includes(i)) {
				const newDeleted = deleted.filter((it) => it !== i)
				setDeleted(newDeleted)
			} else {
				setDeleted([...deleted, i])
			}
		},
		[deleted],
	)

	const handleUpload = useCallback(async () => {
		const pending = changelogs
			.map((changelog, index) => ({ changelog, index }))
			.filter(
				({ index }) => !deleted.includes(index) && !uploaded.includes(index),
			)
		const { batches, tooLarge } = batchChangelogImports(pending)
		const firstTooLarge = tooLarge[0]
		if (firstTooLarge) {
			toast.error({
				title: `"${firstTooLarge.changelog.title}" is too large to import`,
				description:
					"Its description is over 700 KB, usually from embedded images. Link the images by URL instead, or remove this row.",
			})
			return
		}

		analytics.track("upload-changelog-csv")
		cancelledRef.current = false
		setIsUploading(true)
		try {
			for (const batch of batches) {
				if (cancelledRef.current) return
				await bulkCreateChangelogsMutation.mutateAsync({
					changelogs: batch.map((item) => item.changelog),
					portalInstanceId,
				})
				setUploaded((prev) => [...prev, ...batch.map((item) => item.index)])
			}
			onSuccess()
		} catch (error) {
			toast.error({
				title: "Some changelogs didn't import",
				description:
					error instanceof Error && error.message
						? `${error.message} Upload again to import the rest.`
						: "Upload again to import the rest.",
			})
		} finally {
			setIsUploading(false)
		}
	}, [
		changelogs,
		deleted,
		uploaded,
		bulkCreateChangelogsMutation,
		portalInstanceId,
		onSuccess,
	])

	const handleOpenChange = useCallback(() => {
		// If upload is in progress, show confirmation dialog instead of closing
		if (isUploading) {
			setIsConfirmCloseDialogOpen(true)
		} else {
			onOpenChange()
		}
	}, [isUploading, onOpenChange])

	const handleConfirmClose = useCallback(() => {
		setIsConfirmCloseDialogOpen(false)
		cancelledRef.current = true
		onOpenChange()
	}, [onOpenChange])

	const handleCancelClose = useCallback(() => {
		setIsConfirmCloseDialogOpen(false)
	}, [])

	return (
		<>
			<AlertDialog
				isOpen={isConfirmCloseDialogOpen}
				onOpenChange={setIsConfirmCloseDialogOpen}
				title="Upload in progress"
				description="The changelog upload is currently in progress. Are you sure you want to cancel and close?"
				confirmText="Stop & Close"
				cancelText="Continue Upload"
				onConfirm={handleConfirmClose}
				onCancel={handleCancelClose}
			/>

			<Dialog
				title="Preview"
				description="Please remove changelogs you might not want to upload"
				isOpen={isOpen}
				onOpenChange={handleOpenChange}
				footer={
					<div>
						<Button isLoading={isUploading} onClick={() => void handleUpload()}>
							Upload
						</Button>
					</div>
				}
			>
				<div className="space-y-2">
					{changelogs.map((changelog, i) => (
						<div
							key={`changelog-preview-${i}`}
							className="relative flex flex-col overflow-visible rounded-lg border p-2.5 text-sm text-secondary"
						>
							<div className="flex items-center justify-between border-b pb-2">
								<div className="flex-1">
									<div className="font-medium text-primary">
										{changelog.title}
									</div>
									{changelog.dateIso &&
										(() => {
											const parsedDate = parseDate(changelog.dateIso)
											return parsedDate ? (
												<span className="text-xs">
													{format(parsedDate, "MMM d, yyyy")}
												</span>
											) : (
												<span className="text-xs text-red-600">
													Invalid date: {changelog.dateIso}
												</span>
											)
										})()}
									{changelog.published !== undefined && (
										<span
											className={`ml-2 text-xs ${changelog.published ? "text-green-600" : "text-gray-500"}`}
										>
											{changelog.published ? "Published" : "Unpublished"}
										</span>
									)}
								</div>
								<IconButton
									className="z-10"
									onClick={() => handleDelete(i)}
									variant="ghost"
									Icon={deleted.includes(i) ? UndoIcon : Trash2Icon}
								/>
							</div>
							{changelog.description && (
								<div className="mt-2 line-clamp-3">
									{stripHtml(changelog.description)}
								</div>
							)}
							{changelog.imageUrl && (
								<div className="mt-1 truncate text-xs text-green-500">
									Image: {changelog.imageUrl}
								</div>
							)}

							{(deleted.includes(i) || uploaded.includes(i)) && (
								<div className="absolute inset-0 z-1 bg-white/70 dark:bg-gray-900/70" />
							)}
						</div>
					))}
				</div>
			</Dialog>
		</>
	)
}
